/**
 * Suburb-facts — fact-first extraction layer.
 *
 * Why this exists:
 *   Every copy bug we hit (Mernda "no rail option" despite a train station,
 *   Frankston "car or bus slog" despite a terminus) came from the same
 *   shape: heuristic phrasing fired before facts were consulted. The fix is
 *   structural — not "write better copy" but "extract facts first, then
 *   let heuristics *refine* those facts, never replace them."
 *
 * Pipeline:
 *   extractFacts(v)          → TransportFact + SchoolFact + PriceFact
 *         ↓
 *   interpret(facts, mode)   → picks a persona framing around the fact
 *         ↓
 *   render(bestFor, tradeoff) → copy MUST state the fact and MUST NOT
 *                               contradict it
 *
 * Design rules:
 *   - Pure function. No DOM, no network, no schema changes.
 *   - Derived only from existing `tags[]`, `reasons[]`, `priceRange`.
 *   - Facts are *positive signals* with a severity/quality tier; heuristics
 *     downstream can adjust framing but cannot flip the boolean
 *     (has-rail true → downstream copy can never say "no rail").
 *   - When a fact is unknown/thin, return `null` so the generator can fall
 *     back to a generic line — never guess a fact.
 */

import type { Verdict } from "../data/verdicts";

/* -------------------------------------------------------------------------- */
/*  Facts                                                                     */
/* -------------------------------------------------------------------------- */

export type CommuteQuality = "short" | "medium" | "long" | "unknown";

export interface TransportFact {
  /** True only when the positive rail/tram/metro signal is present and the */
  /** reason text does NOT explicitly disclaim it ("no train", "bus-only"). */
  hasRail: boolean;
  /** True when reason text names a terminus or the `end-of-line` tag fires. */
  terminus: boolean;
  /** True for the `single-line` tag — single point of failure. */
  singleLine: boolean;
  /** True when rail is absent and bus connects out to rail. */
  busOnly: boolean;
  /** Qualitative commute length, derived from reason text + tags. */
  commuteQuality: CommuteQuality;
  /** Extracted peak-commute minutes if a range is present (e.g. 45–55 → 50). */
  commuteMinutes: number | null;
  /** True when freeway-parallel / arterial-noise signals fire. */
  freewayAdjacent: boolean;
  /** Short ready-to-embed phrase describing the transport fact. */
  summary: string;
}

export interface SchoolFactCore {
  /** True when a public catchment or named school is surfaced. */
  hasCatchment: boolean;
  /** True when reasons explicitly disclaim a catchment. */
  disclaimed: boolean;
  /** True when the only schools named are private/international. */
  privateOnly: boolean;
  /** True when growth-corridor + primaries-only signal ("secondary forming"). */
  emerging: boolean;
  summary: string;
}

export type PriceBand = "entry" | "mid" | "premium" | "unknown";

export interface PriceFact {
  band: PriceBand;
  /** Midpoint of the priceRange numeric tokens (unit-agnostic). */
  mid: number | null;
  /** Currency prefix extracted from priceRange (A$ / ₹ / $). */
  currency: string;
  summary: string;
}

export interface Facts {
  transport: TransportFact;
  school: SchoolFactCore;
  price: PriceFact;
}

/* -------------------------------------------------------------------------- */
/*  Low-level helpers                                                         */
/* -------------------------------------------------------------------------- */

function tagSet(v: Verdict): Set<string> {
  return new Set((v.tags || []).map((t) => t.toLowerCase()));
}
function reasonBlob(v: Verdict): string {
  return (v.reasons || []).join(" ").toLowerCase();
}

const RAIL_REASON_RE =
  /\b(train|rail|metro|tram|line\b|station|terminus|v\s*\/?\s*line)\b/i;
const NO_RAIL_REASON_RE = /\b(no\s+train|no\s+rail|bus[-\s]only)\b/i;
const TERMINUS_RE = /\bterminus\b/i;
const MINUTE_RE = /(\d{1,3})(?:\s*[–-]\s*(\d{1,3}))?\s*min\b/i;

/* -------------------------------------------------------------------------- */
/*  Transport fact                                                            */
/* -------------------------------------------------------------------------- */

function extractTransport(v: Verdict): TransportFact {
  const t = tagSet(v);
  const reasons = reasonBlob(v);

  // Rail — positive signal first, negative reason phrase can veto.
  const deniesRail = NO_RAIL_REASON_RE.test(reasons);
  const tagRail =
    t.has("transit") ||
    t.has("tram-corridor") ||
    t.has("metro-tunnel") ||
    t.has("metro");
  const reasonRail = RAIL_REASON_RE.test(reasons);
  const hasRail = !deniesRail && (tagRail || reasonRail);

  const terminus = t.has("end-of-line") || TERMINUS_RE.test(reasons);
  const singleLine = t.has("single-line");
  const busOnly =
    !hasRail && (t.has("bus-only") || /\bbus[-\s]only\b/i.test(reasons));

  // Commute minutes — grab the largest of the range if present.
  let commuteMinutes: number | null = null;
  const m = reasons.match(MINUTE_RE);
  if (m) {
    const a = parseInt(m[1], 10);
    const b = m[2] ? parseInt(m[2], 10) : a;
    commuteMinutes = Math.max(a, b);
  }

  // Commute quality — tag wins, reason text seconds.
  let commuteQuality: CommuteQuality = "unknown";
  if (t.has("long-commute")) commuteQuality = "long";
  else if (commuteMinutes !== null) {
    commuteQuality =
      commuteMinutes >= 45 ? "long" :
      commuteMinutes >= 25 ? "medium" :
      "short";
  } else if (t.has("cbd") || t.has("inner-city")) {
    commuteQuality = "short";
  }

  const freewayAdjacent =
    t.has("freeway-dependent") ||
    t.has("arterial-noise") ||
    /\b(freeway|fwy|m\d\b)\b/i.test(reasons);

  // Summary — stitched from the true facts. Never asserts absence of rail
  // when rail is present; never asserts rail when deniesRail fires.
  const parts: string[] = [];
  if (hasRail) {
    if (terminus) parts.push("rail terminus");
    else if (singleLine) parts.push("single rail line");
    else parts.push("rail access");
  } else if (busOnly) {
    parts.push("bus-only to nearest station");
  } else {
    parts.push("no in-suburb rail");
  }
  if (commuteMinutes !== null) {
    parts.push(`${commuteMinutes}+ min peak to CBD`);
  } else if (commuteQuality === "long") {
    parts.push("long peak commute");
  }
  if (freewayAdjacent && !hasRail) parts.push("freeway-dependent");

  return {
    hasRail,
    terminus,
    singleLine,
    busOnly,
    commuteQuality,
    commuteMinutes,
    freewayAdjacent,
    summary: parts.join(", "),
  };
}

/* -------------------------------------------------------------------------- */
/*  School fact core (structural only; marquee catalog still wraps it).       */
/* -------------------------------------------------------------------------- */

const NAMED_SCHOOL_RE =
  /\b(grammar|college|international school|K[–-]?12|kendriya|\bDPS\b|orchids|army public|scotch|mlc|xavier|trinity|presbyterian)\b/i;
const DISCLAIM_CATCHMENT_RE =
  /\b(no\s+(public\s+)?catchment|commute\s+kids\s+out)\b/i;

function extractSchool(v: Verdict): SchoolFactCore {
  const t = tagSet(v);
  const reasons = reasonBlob(v);

  const hasTag = t.has("school-catchment") || t.has("family");
  const namedInReasons = NAMED_SCHOOL_RE.test(reasons);
  const disclaimed = DISCLAIM_CATCHMENT_RE.test(reasons);

  const privateOnly =
    namedInReasons &&
    /\b(grammar|college|international|K[–-]?12|DPS|orchids|scotch|mlc|xavier)\b/i.test(
      reasons
    ) &&
    !/\b(zone|catchment|primary|secondary|public)\b/i.test(reasons);

  const emerging =
    (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) &&
    !hasTag &&
    !namedInReasons;

  const hasCatchment = !disclaimed && (hasTag || namedInReasons);

  const summary = disclaimed
    ? "no public-school zone advantage; kids commute out"
    : privateOnly
    ? "private schools only"
    : hasCatchment
    ? "zoned to named public schools"
    : emerging
    ? "new-estate primaries; secondary forming"
    : "no standout public-school zone";

  return { hasCatchment, disclaimed, privateOnly, emerging, summary };
}

/* -------------------------------------------------------------------------- */
/*  Price fact                                                                */
/* -------------------------------------------------------------------------- */

function extractPrice(v: Verdict): PriceFact {
  const t = tagSet(v);
  const range = v.priceRange;
  const currency = /₹/.test(range) ? "₹" : /A\$/.test(range) ? "A$" : /\$/.test(range) ? "$" : "";
  const nums = [...range.matchAll(/(\d+(?:\.\d+)?)/g)].map((x) => parseFloat(x[1]));
  const mid = nums.length ? (nums[0] + nums[nums.length - 1]) / 2 : null;

  // Band — tag-first so `premium-inner` always lands premium regardless of
  // unit, and a low `mid` in ₹/sqft units isn't mistaken for low-band AUD.
  let band: PriceBand = "unknown";
  if (t.has("premium-inner") || (mid !== null && mid >= 22)) band = "premium";
  else if (mid !== null && mid <= 8) band = "entry";
  else if (mid !== null) band = "mid";

  const summary = `${currency}${range.replace(/\s+/g, " ").trim()}`;
  return { band, mid, currency, summary };
}

/* -------------------------------------------------------------------------- */
/*  Public                                                                    */
/* -------------------------------------------------------------------------- */

export function extractFacts(v: Verdict): Facts {
  return {
    transport: extractTransport(v),
    school: extractSchool(v),
    price: extractPrice(v),
  };
}

/**
 * Stitch a "facts first" prefix used to anchor copy. The generators below
 * state this verbatim in the tradeoff so users always see the positive
 * fact before the friction phrase.
 */
export function factPrefixCommute(f: TransportFact): string {
  if (f.hasRail) {
    if (f.terminus) return "rail terminus here";
    if (f.singleLine) return "single rail line";
    return "rail access here";
  }
  if (f.busOnly) return "bus-only to rail";
  return "no in-suburb rail";
}
